import {
	AfterViewInit,
	Component,
	ElementRef,
	HostBinding,
	InputSignal,
	Injector,
	OnDestroy,
	Signal,
	ViewChild
} from '@angular/core';
import { computed, effect, inject, input, runInInjectionContext, signal, untracked, WritableSignal } from '@angular/core';
import Cropper from 'cropperjs';
import { PhLoaderComponent } from '../../ph-loader/ph-loader.component';
import {
	canvasToPhotoEditorDataUrl,
	mapNaturalRectToCanvasSelectionSpace,
	maxInscribedRectWithAspectRatio,
	resolveStretchHandles,
	resolveTrimContentNatural,
	transformAfterStretchDrag,
	asPhotoEditorCropper,
	cropperZoomArgumentForUniformScale, resolveMatrixScale
} from './photo-editor.utils';
import {
	mergePhotoEditorConfig,
	PHOTO_EDITOR_CONFIG, PHOTO_EDITOR_CROPPER_TEMPLATE,
	type PhotoEditorConfig,
	type PhotoEditorConfigInput
} from './photo-editor.config';
import { bindPhotoEditorHotkeys, DEFAULT_PAN_ARROW_STEP_PX } from './photo-editor-hotkeys';
import {
	AxisBounds,
	CanvasSelectionRect,
	type CropperCanvasLike,
	type CropperImageElement,
	type CropperSelectionLike, MatrixScale,
	type PhotoEditorCropperCanvasAction,
	type PhotoEditorCropperFacade,
	type PhotoEditorImageFrame, PhotoEditorSelectionRect,
	type PhotoEditorStretchHandle,
	type PhotoEditorStretchHandlePoint, PhotoEditorStretchSession,
	TransformMatrix2D, VectorAxis
} from './photo-editor.types';
import { revokeBlobObjectUrl } from '../product-photo.utils';

@Component({
	selector: 'app-photo-editor',
	templateUrl: './photo-editor.component.html',
	styleUrls: ['./photo-editor.component.scss'],
	standalone: true,
	imports: [
		PhLoaderComponent
	]
})
export class PhotoEditorComponent implements AfterViewInit, OnDestroy {
	private readonly injector: Injector = inject(Injector);

	@HostBinding('class.photo-editor--aspect-locked')
	get hostAspectLocked(): boolean {
		return this.isAspectLocked();
	}

	@HostBinding('class.photo-editor--stretch-mode')
	get hostStretchMode(): boolean {
		return this.imageStretchMode();
	}

	@HostBinding('class.photo-editor--pan-active')
	get hostPanCursor(): boolean {
		return this.imagePanMode();
	}

	readonly imageSrc: InputSignal<string> = input<string>('');
	readonly editorImageSrc: WritableSignal<string> = signal('');
	readonly aspectRatio: InputSignal<number> = input<number>(undefined);
	readonly editorConfig: InputSignal<PhotoEditorConfigInput> = input<PhotoEditorConfigInput>(undefined);
	readonly showToolsPanel: InputSignal<boolean> = input(true);
	readonly autoPan: InputSignal<boolean> = input(false);

	@ViewChild('editorImage') editorImage!: ElementRef<HTMLImageElement>;

	private cropper: Cropper = null;

	readonly loading: WritableSignal<boolean> = signal(true);
	readonly imagePanMode: WritableSignal<boolean> = signal(false);
	readonly imageStretchMode: WritableSignal<boolean> = signal(false);
	readonly stretchHandlePoints: WritableSignal<ReadonlyArray<PhotoEditorStretchHandlePoint>> = signal<ReadonlyArray<PhotoEditorStretchHandlePoint>>([]);
	readonly imageScaleXPercent: WritableSignal<number> = signal(100);
	readonly imageScaleYPercent: WritableSignal<number> = signal(100);
	readonly imageRotationDeg: WritableSignal<number> = signal(0);
	readonly hasUnsavedChanges: WritableSignal<boolean> = signal(false);
	private readonly imageRotationDegExact: WritableSignal<number> = signal(0);
	private baseImageScaleX: number = 1;
	private baseImageScaleY: number = 1;
	readonly maskMode: WritableSignal<'product' | 'image' | 'free'> = signal<'product' | 'image' | 'free'>('product');
	private productMaskInitialized: boolean = false;

	private readonly effectiveAspectRatio: Signal<number> = computed((): number => {
		const r: number = this.aspectRatio();
		if (r == null || !Number.isFinite(r) || r <= 0) return undefined;
		return r;
	});
	private readonly activeAspectRatio: Signal<number> = computed((): number => (this.maskMode() === 'product' ? this.effectiveAspectRatio() : undefined));
	private readonly isAspectLocked: Signal<boolean> = computed((): boolean => this.activeAspectRatio() !== undefined);
	private stretchDragSession: PhotoEditorStretchSession = null;
	private stretchFallbackTransform: TransformMatrix2D = null;
	private readonly onWindowPointerMoveBound: (event: PointerEvent) => void = (event: PointerEvent): void =>
		this.onStretchPointerMove(event);
	private readonly onWindowPointerUpBound: (event: PointerEvent) => void = (event: PointerEvent): void =>
		this.onStretchPointerUpOrCancel(event);
	private readonly minStretchScale: number = 0.03;
	private readonly zoomStep: number = 0.05;

	private mountGeneration: number = 0;
	private canvasResizeObserver: ResizeObserver = null;
	private resizeDebounceTimer: ReturnType<typeof setTimeout> = null;
	private canvasPointerRefreshCleanup: (() => void) = null;
	private canvasPanStretchRefreshRafId: number = null;
	private isClampingSelection: boolean = false;
	private readonly patchedSelections: WeakSet<object> = new WeakSet<object>();

	private hotkeysUnsubscribe: (() => void) = null;
	private resolvedBlobUrl: string = null;
	private editorSrcResolveToken: number = 0;
	private baselineSnapshot: string = '';
	private dirtyTrackingReady: boolean = false;

	private get cfg(): PhotoEditorConfig {
		return mergePhotoEditorConfig(PHOTO_EDITOR_CONFIG, this.editorConfig());
	}

	private get api(): PhotoEditorCropperFacade {
		return asPhotoEditorCropper(this.cropper);
	}

	private getCropperImage(): CropperImageElement {
		return this.api?.getCropperImage?.() as CropperImageElement;
	}

	private getCropperSelection(): CropperSelectionLike {
		return this.api?.getCropperSelection?.();
	}

	private getCropperCanvas(): CropperCanvasLike {
		return this.api?.getCropperCanvas?.();
	}

	ngAfterViewInit(): void {
		this.registerSignals();
		void this.resolveEditorImageSrc(this.imageSrc());
		this.init();
		this.registerToolbarHotkeys();
	}

	ngOnDestroy(): void {
		this.unregisterToolbarHotkeys();
		this.mountGeneration++;
		this.loading.set(false);
		this.destroy();
		this.revokeResolvedBlobUrl();
	}

	onImageLoad(): void {
		this.init();
	}

	onImageError(): void {
		this.loading.set(false);
	}

	async getEditedImageDataUrl(): Promise<string> {
		const selection: CropperSelectionLike = this.getCropperSelection();
		if (!selection?.$toCanvas) return undefined;
		const canvas: HTMLCanvasElement = await selection.$toCanvas();
		return (
			canvasToPhotoEditorDataUrl(canvas, this.cfg.output.jpegQuality) ||
			this.editorImageSrc() ||
			this.imageSrc() ||
			undefined
		);
	}

	zoomIn(): void {
		this.zoomByPercentStep(1);
	}

	zoomOut(): void {
		this.zoomByPercentStep(-1);
	}

	setMaskMode(mode: 'product' | 'image' | 'free'): void {
		if (this.maskMode() === mode) return;
		this.maskMode.set(mode);
		this.applyMaskMode(mode);
	}

	resetAll(): void {
		if (!this.cropper) return;
		this.productMaskInitialized = false;
		this.maskMode.set('product');
		this.imagePanMode.set(true);
		this.imageStretchMode.set(true);

		this.fitImageContain();

		const selection: CropperSelectionLike = this.getCropperSelection();
		const ar: number = this.effectiveAspectRatio();
		if (selection) {
			if (ar != null) {
				selection.aspectRatio = ar;
				selection.movable = false;
				selection.resizable = false;
				this.autoTrimEmptyEdges({ recenterImage: true, constrainToCanvasPadding: true });
				this.productMaskInitialized = true;
			} else {
				selection.aspectRatio = Number.NaN;
				selection.movable = false;
				selection.resizable = false;
				this.setSelectionToFullImage();
			}
		}

		this.syncCropperImageTransformFlags();
		this.syncTransformState();
		requestAnimationFrame((): void => this.setBaselineSnapshot());
	}

	rotateBy90(dir: -1 | 1): void {
		this.rotateBy(dir * 90);
	}

	setRotationDeg(targetDeg: number): void {
		if (!Number.isFinite(targetDeg)) return;
		this.applyRotationTo(this.normalizeRotationDeg(targetDeg));
	}

	private rotateBy(deltaDeg: number): void {
		this.applyRotationTo(this.imageRotationDegExact() + deltaDeg);
	}

	private applyRotationTo(targetDeg: number): void {
		const img: CropperImageElement = this.getCropperImage();
		if (!img) return;
		const clamped: number = this.normalizeRotationDeg(targetDeg);
		img.rotatable = true;
		img.scalable = true;
		img.translatable = true;

		const m: number[] = img.$getTransform?.();
		if (!m || m.length < 6 || !img.$setTransform) {
			const curr: number = this.imageRotationDegExact();
			img.$rotate?.(clamped - curr);
			this.scheduleAfterTransformSync();
			return;
		}

		const [a, b, c, d, e, f] = m as number[];
		if (![a, b, c, d, e, f].every((v: number): boolean => Number.isFinite(v))) return;

		const scaleX: number = Math.sqrt(a * a + b * b) || 1;
		const scaleY: number = Math.sqrt(c * c + d * d) || 1;
		const rad: number = (clamped * Math.PI) / 180;
		const na: number = scaleX * Math.cos(rad);
		const nb: number = scaleX * Math.sin(rad);
		const nc: number = -scaleY * Math.sin(rad);
		const nd: number = scaleY * Math.cos(rad);
		img.$setTransform(na, nb, nc, nd, e, f);
		this.scheduleAfterTransformSync();
	}

	private normalizeRotationDeg(value: number): number {
		const norm: number = ((value % 360) + 360) % 360;
		return norm > 180 ? norm - 360 : norm;
	}

	private registerToolbarHotkeys(): void {
		this.unregisterToolbarHotkeys();
		this.hotkeysUnsubscribe = bindPhotoEditorHotkeys(window, {
			onZoomIn: (): void => this.zoomByPercentStep(1),
			onZoomOut: (): void => this.zoomByPercentStep(-1),
			onPanArrow: (dirX: number, dirY: number): void => this.nudgeImagePanByArrows(dirX, dirY),
			isPanArrowActive: (): boolean => !this.loading()
		});
	}

	private nudgeImagePanByArrows(dirX: number, dirY: number): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		if (!cropImg?.$move) return;
		const step: number = DEFAULT_PAN_ARROW_STEP_PX;
		cropImg.scalable = true;
		cropImg.translatable = true;
		cropImg.$move(dirX * step, dirY * step);
		this.syncCropperImageTransformFlags();
	}

	private unregisterToolbarHotkeys(): void {
		this.hotkeysUnsubscribe?.();
		this.hotkeysUnsubscribe = null;
	}

	toggleImagePanMode(): void {
		if (!this.getCropperImage()) return;
		this.imagePanMode.update((v: boolean): boolean => !v);
		this.cancelStretchDragSession();
		this.applySelectionMovableResizableForModes();
		this.syncCropperImageTransformFlags();
	}

	toggleImageStretchMode(): void {
		if (!this.getCropperImage()) return;
		this.imageStretchMode.update((v: boolean): boolean => !v);
		this.applySelectionMovableResizableForModes();
		this.cancelStretchDragSession();
		this.syncCropperImageTransformFlags();
	}

	private applySelectionMovableResizableForModes(): void {
		const selection: CropperSelectionLike = this.getCropperSelection();
		if (!selection) return;
		if (this.isAspectLocked() || this.imagePanMode() || this.imageStretchMode()) {
			selection.movable = false;
			selection.resizable = false;
			return;
		}
		selection.movable = true;
		selection.resizable = true;
	}

	private zoomByPercentStep(direction: -1 | 1): void {
		const nextPctX: number = (this.imageScaleXPercent() || 100) + direction * this.zoomStep * 100;
		const nextPctY: number = (this.imageScaleYPercent() || 100) + direction * this.zoomStep * 100;
		this.applyScalePercents(nextPctX, nextPctY);
	}

	private applyScalePercents(percentX: number, percentY: number): void {
		const img: CropperImageElement = this.getCropperImage();
		const m: number[] = img?.$getTransform?.();
		if (!img || !m || m.length < 6 || !img.$setTransform) return;
		const [a, b, c, d, e, f] = m as number[];
		if (![a, b, c, d, e, f].every((v: number): boolean => Number.isFinite(v))) return;

		const curScaleX: number = Math.sqrt(a * a + b * b);
		const curScaleY: number = Math.sqrt(c * c + d * d);
		if (!Number.isFinite(curScaleX) || !Number.isFinite(curScaleY) || curScaleX <= 0 || curScaleY <= 0) return;

		const ux: number = a / curScaleX;
		const uy: number = b / curScaleX;
		const vx: number = c / curScaleY;
		const vy: number = d / curScaleY;

		const baseX: number = Number.isFinite(this.baseImageScaleX) && this.baseImageScaleX > 0 ? this.baseImageScaleX : curScaleX;
		const baseY: number = Number.isFinite(this.baseImageScaleY) && this.baseImageScaleY > 0 ? this.baseImageScaleY : curScaleY;
		const targetScaleX: number = Math.max(0.01, (Math.max(1, Math.min(999, Math.round(percentX))) / 100) * baseX);
		const targetScaleY: number = Math.max(0.01, (Math.max(1, Math.min(999, Math.round(percentY))) / 100) * baseY);

		img.scalable = true;
		img.$setTransform(ux * targetScaleX, uy * targetScaleX, vx * targetScaleY, vy * targetScaleY, e, f);
		this.scheduleStretchHandlesRefresh();
		this.scheduleAfterTransformSync();
	}

	private init(): void {
		if (!this.editorImage?.nativeElement || !this.editorImageSrc()) return;
		this.cropper?.destroy();
		this.cropper = null;
		this.productMaskInitialized = false;
		this.imagePanMode.set(false);
		this.imageStretchMode.set(false);
		this.stretchHandlePoints.set([]);
		this.stretchFallbackTransform = null;
		this.baseImageScaleX = 1;
		this.baseImageScaleY = 1;
		this.imageScaleXPercent.set(100);
		this.imageScaleYPercent.set(100);
		this.imageRotationDeg.set(0);
		this.baselineSnapshot = '';
		this.dirtyTrackingReady = false;
		this.hasUnsavedChanges.set(false);
		this.cancelStretchDragSession();
		this.loading.set(true);
		const generation: number = ++this.mountGeneration;
		this.cropper = new Cropper(this.editorImage.nativeElement, {
			template: PHOTO_EDITOR_CROPPER_TEMPLATE
		});
		this.waitForCropperImageThenBootstrap(generation);
	}

	private waitForCropperImageThenBootstrap(generation: number): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		const htmlImg: HTMLImageElement = this.editorImage?.nativeElement;
		const bootstrap: () => void = (): void => {
			if (generation !== this.mountGeneration || !this.cropper) return;
			this.bootstrapCropperAfterMount(generation);
		};
		if (cropImg?.$ready) {
			void cropImg
				.$ready()
				.then((): void => bootstrap())
				.catch((): void => {
					if (generation !== this.mountGeneration) return;
					this.loading.set(false);
				});
			return;
		}
		const inner: HTMLImageElement = cropImg?.$image;
		const nw: number = inner?.naturalWidth ?? htmlImg?.naturalWidth ?? 0;
		if (nw > 0 || (htmlImg?.complete && (htmlImg.naturalWidth ?? 0) > 0)) {
			queueMicrotask((): void => bootstrap());
			return;
		}
		htmlImg?.addEventListener('load', (): void => bootstrap(), { once: true });
	}

	private bootstrapCropperAfterMount(generation: number): void {
		if (generation !== this.mountGeneration || !this.cropper) return;
		this.configureSelection({ autoTrimEdges: true });
	}

	private configureSelection(options: { autoTrimEdges: boolean }): void {
		const selection: CropperSelectionLike = this.getCropperSelection();
		if (!selection) return;
		this.patchSelectionChangeWithCanvasBounds();
		const ar: number = this.activeAspectRatio();
		if (ar != null) {
			selection.aspectRatio = ar;
			selection.resizable = false;
			selection.movable = false;
		} else {
			selection.aspectRatio = Number.NaN;
			selection.resizable = !this.imagePanMode() && !this.imageStretchMode();
			selection.movable = !this.imagePanMode() && !this.imageStretchMode();
		}
		this.syncCropperImageTransformFlags();
		requestAnimationFrame((): void => this.clampSelectionToCanvas());
		if (options.autoTrimEdges) this.scheduleAutoTrimAfterLayout();
	}

	private syncCropperImageTransformFlags(): void {
		const img: CropperImageElement = this.getCropperImage();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		if (!img) return;
		img.scalable = true;
		img.translatable = this.imagePanMode();
		const action: PhotoEditorCropperCanvasAction =
			this.imagePanMode()
				? 'move'
				: this.maskMode() === 'free'
					? 'select'
					: 'none';
		canvas?.$setAction?.(action);
		this.refreshStretchHandles();
		this.syncTransformState();
	}

	private scheduleAfterTransformSync(): void {
		requestAnimationFrame((): void => {
			this.refreshStretchHandles();
			this.syncTransformState();
			this.clampSelectionToCanvas();
		});
	}

	private clampSelectionToCanvas(): void {
		if (this.isClampingSelection) return;
		const selection: CropperSelectionLike = this.getCropperSelection();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		if (!selection?.$change || !canvas) return;

		const canvasW: number = Math.max(canvas.offsetWidth, 1);
		const canvasH: number = Math.max(canvas.offsetHeight, 1);
		let x: number = Number.isFinite(selection.x) ? selection.x : 0;
		let y: number = Number.isFinite(selection.y) ? selection.y : 0;
		let w: number = Number.isFinite(selection.width) ? selection.width : canvasW;
		let h: number = Number.isFinite(selection.height) ? selection.height : canvasH;

		const minRectPx: number = Math.max(1, this.cfg.layout.minCropRectPx);
		w = Math.max(minRectPx, Math.min(w, canvasW));
		h = Math.max(minRectPx, Math.min(h, canvasH));
		x = Math.max(0, Math.min(x, canvasW - w));
		y = Math.max(0, Math.min(y, canvasH - h));

		const changed: boolean =
			Math.abs(selection.x - x) > 0.001 ||
			Math.abs(selection.y - y) > 0.001 ||
			Math.abs(selection.width - w) > 0.001 ||
			Math.abs(selection.height - h) > 0.001;
		if (!changed) return;

		this.isClampingSelection = true;
		try {
			selection.$change(x, y, w, h);
		} finally {
			this.isClampingSelection = false;
		}
	}

	private patchSelectionChangeWithCanvasBounds(): void {
		const selection: 
			| (CropperSelectionLike & {
					x: number;
					y: number;
					width: number;
					height: number;
					$change: (
						x: number,
						y: number,
						width?: number,
						height?: number,
						aspectRatio?: number,
						force?: boolean
					) => unknown;
			  })
			| undefined = this.getCropperSelection() as
			| (CropperSelectionLike & {
					x: number;
					y: number;
					width: number;
					height: number;
					$change: (
						x: number,
						y: number,
						width?: number,
						height?: number,
						aspectRatio?: number,
						force?: boolean
					) => unknown;
			  })
			| undefined;
		if (!selection?.$change) return;
		if (this.patchedSelections.has(selection)) return;

		const originalChange: (
			x: number,
			y: number,
			width?: number,
			height?: number,
			aspectRatio?: number,
			force?: boolean
		) => unknown = selection.$change.bind(selection);
		selection.$change = (
			x: number,
			y: number,
			width?: number,
			height?: number,
			aspectRatio?: number,
			force?: boolean
		): unknown => {
			const canvas: CropperCanvasLike = this.getCropperCanvas();
			if (!canvas) return originalChange(x, y, width, height, aspectRatio, force);

			const canvasW: number = Math.max(canvas.offsetWidth, 1);
			const canvasH: number = Math.max(canvas.offsetHeight, 1);
			const minRectPx: number = Math.max(1, this.cfg.layout.minCropRectPx);
			const nextW: number = Math.max(
				minRectPx,
				Math.min(Number.isFinite(width as number) ? (width as number) : selection.width, canvasW)
			);
			const nextH: number = Math.max(
				minRectPx,
				Math.min(Number.isFinite(height as number) ? (height as number) : selection.height, canvasH)
			);
			const nextX: number = Math.max(0, Math.min(Number.isFinite(x) ? x : selection.x, canvasW - nextW));
			const nextY: number = Math.max(0, Math.min(Number.isFinite(y) ? y : selection.y, canvasH - nextH));
			return originalChange(nextX, nextY, nextW, nextH, aspectRatio, force);
		};

		this.patchedSelections.add(selection);
	}

	private syncTransformState(): void {
		const img: CropperImageElement = this.getCropperImage();
		const m: number[] = img?.$getTransform?.();

		if (!m || m.length < 6) return;

		const scale: MatrixScale | null = resolveMatrixScale(
			m[0],
			m[1],
			m[2],
			m[3]
		);

		if (!scale) return;

		const scaleX: number = scale.scaleX;
		const scaleY: number = scale.scaleY;

		const baseX: number =
			Number.isFinite(this.baseImageScaleX) && this.baseImageScaleX > 0
				? this.baseImageScaleX
				: scaleX || 1;

		const baseY: number =
			Number.isFinite(this.baseImageScaleY) && this.baseImageScaleY > 0
				? this.baseImageScaleY
				: scaleY || 1;

		const angleRad: number = Math.atan2(m[1], m[0]);
		const angleDeg: number = (angleRad * 180) / Math.PI;

		const pctX: number = Math.round((scaleX / baseX) * 100);
		const pctY: number = Math.round((scaleY / baseY) * 100);

		this.imageScaleXPercent.set(
			Number.isFinite(pctX)
				? Math.max(1, Math.min(999, pctX))
				: 100
		);

		this.imageScaleYPercent.set(
			Number.isFinite(pctY)
				? Math.max(1, Math.min(999, pctY))
				: 100
		);

		const norm: number = ((angleDeg % 360) + 360) % 360;
		const signed: number = norm > 180 ? norm - 360 : norm;

		this.imageRotationDegExact.set(signed);
		this.imageRotationDeg.set(Math.round(signed));
		this.refreshUnsavedChangesState();
	}

	private buildStateSnapshot(): string | null {
		const img: CropperImageElement = this.getCropperImage();
		const selection: CropperSelectionLike = this.getCropperSelection();
		const transform: number[] = img?.$getTransform?.();
		if (!transform || transform.length < 6 || !selection) return null;

		const roundedTransform: string = transform
			.slice(0, 6)
			.map((v: number): string => (Number.isFinite(v) ? v.toFixed(4) : 'NaN'))
			.join(',');
		const selectionState: string = [
			Number.isFinite(selection.x) ? selection.x.toFixed(2) : 'NaN',
			Number.isFinite(selection.y) ? selection.y.toFixed(2) : 'NaN',
			Number.isFinite(selection.width) ? selection.width.toFixed(2) : 'NaN',
			Number.isFinite(selection.height) ? selection.height.toFixed(2) : 'NaN'
		].join(',');

		return `${this.maskMode()}|${roundedTransform}|${selectionState}`;
	}

	private refreshUnsavedChangesState(): void {
		if (!this.dirtyTrackingReady) {
			this.hasUnsavedChanges.set(false);
			return;
		}

		const snapshot: string | null = this.buildStateSnapshot();
		if (!snapshot) {
			this.hasUnsavedChanges.set(false);
			return;
		}

		this.hasUnsavedChanges.set(snapshot !== this.baselineSnapshot);
	}

	private setBaselineSnapshot(): void {
		const snapshot: string | null = this.buildStateSnapshot();
		if (!snapshot) return;
		this.baselineSnapshot = snapshot;
		this.dirtyTrackingReady = true;
		this.hasUnsavedChanges.set(false);
	}

	private captureBaseImageScale(): void {
		const img: CropperImageElement = this.getCropperImage();
		const m: number[] = img?.$getTransform?.();

		if (!m || m.length < 4) return;

		const [a, b, c, d] = m;

		const scale: MatrixScale | null = resolveMatrixScale(a, b, c, d);

		if (!scale) return;

		if (scale.scaleX > 0) {
			this.baseImageScaleX = scale.scaleX;
		}

		if (scale.scaleY > 0) {
			this.baseImageScaleY = scale.scaleY;
		}
	}

	private applyMaskMode(mode: 'product' | 'image' | 'free'): void {
		if (!this.cropper) return;
		const selection: CropperSelectionLike = this.getCropperSelection();
		if (!selection) return;
		const ar: number = this.activeAspectRatio();

		if (mode === 'free') {
			selection.aspectRatio = Number.NaN;
			this.imagePanMode.set(false);
			this.imageStretchMode.set(false);
			selection.movable = true;
			selection.resizable = true;
			this.syncCropperImageTransformFlags();
			return;
		}

		this.imagePanMode.set(true);
		this.imageStretchMode.set(true);

		if (mode === 'product' && ar != null) {
			selection.aspectRatio = ar;
			selection.movable = false;
			selection.resizable = false;
			const recenterImage: boolean = !this.productMaskInitialized;
			this.autoTrimEmptyEdges({ recenterImage, constrainToCanvasPadding: true });
			this.productMaskInitialized = true;
			this.syncCropperImageTransformFlags();
			return;
		}

		selection.aspectRatio = Number.NaN;
		selection.movable = false;
		selection.resizable = false;
		this.setSelectionToFullImage();
		this.syncCropperImageTransformFlags();
	}

	private setSelectionToFullImage(): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		const selection: CropperSelectionLike = this.getCropperSelection();
		const htmlImg: HTMLImageElement = this.editorImage?.nativeElement;
		if (!cropImg?.$getTransform || !selection?.$change || !htmlImg) return;
		const nw: number = htmlImg.naturalWidth;
		const nh: number = htmlImg.naturalHeight;
		if (nw <= 0 || nh <= 0) return;
		const box: CanvasSelectionRect =
			mapNaturalRectToCanvasSelectionSpace(cropImg, nw, nh, 0, 0, nw, nh, this.cfg.layout.minCropRectPx);
		if (!box) return;
		selection.$change(box.sx, box.sy, box.sw, box.sh);
	}

	private queuePatchCropperImageShadowPointerEvents(attempt: number = 0): void {
		const host: CropperImageElement = this.getCropperImage();
		const img: HTMLImageElement = host?.shadowRoot?.querySelector('img');
		if (img) {
			img.style.pointerEvents = 'none';
			return;
		}
		if (attempt < 25) {
			requestAnimationFrame((): void => this.queuePatchCropperImageShadowPointerEvents(attempt + 1));
		}
	}

	private scheduleAutoTrimAfterLayout(): void {
		const gen: number = this.mountGeneration;
		const cropImg: CropperImageElement = this.getCropperImage();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		const maxAttempts: number = this.cfg.layout.canvasReadyMaxAttempts;

		const finishReveal: () => void = (): void => {
			requestAnimationFrame((): void => {
				requestAnimationFrame((): void => {
					if (gen !== this.mountGeneration) return;
					if (!this.showToolsPanel() || this.autoPan()) {
						this.imagePanMode.set(true);
						this.imageStretchMode.set(true);
						this.applySelectionMovableResizableForModes();
					}
					this.applyMaskMode(this.maskMode());
					this.syncCropperImageTransformFlags();
					this.queuePatchCropperImageShadowPointerEvents();
					this.attachCanvasResizeObserver();
					this.loading.set(false);
					requestAnimationFrame((): void => this.setBaselineSnapshot());
				});
			});
		};

		const runTrim: () => void = (): void => {
			if (gen !== this.mountGeneration) return;
			if (this.isAspectLocked()) {
				this.stabilizeImageCenterAfterLayout(gen, finishReveal);
				return;
			}
			this.fitImageContain();
			this.autoTrimEmptyEdges();
			finishReveal();
		};

		const waitCanvasThenTrim: (attempt: number) => void = (attempt: number): void => {
			if (gen !== this.mountGeneration) return;
			const w: number = canvas?.offsetWidth ?? 0;
			const h: number = canvas?.offsetHeight ?? 0;
			if (canvas && (w < 2 || h < 2) && attempt < maxAttempts) {
				requestAnimationFrame((): void => waitCanvasThenTrim(attempt + 1));
				return;
			}
			runTrim();
		};

		const afterPaint: () => void = (): void => {
			requestAnimationFrame((): number =>
				requestAnimationFrame((): void => {
					waitCanvasThenTrim(0);
					this.clampSelectionToCanvas();
				})
			);
		};

		if (cropImg?.$nextTick) void cropImg.$nextTick(afterPaint);
		else afterPaint();
	}

	autoTrimEmptyEdges(options?: { recenterImage?: boolean; constrainToCanvasPadding?: boolean }): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		const selection: CropperSelectionLike = this.getCropperSelection();
		const htmlImg: HTMLImageElement = this.editorImage?.nativeElement;
		const canvasEl: CropperCanvasLike = this.getCropperCanvas();
		if (!cropImg?.$getTransform || !selection?.$change || !htmlImg) return;

		const nw: number = htmlImg.naturalWidth;
		const nh: number = htmlImg.naturalHeight;
		if (nw <= 0 || nh <= 0) return;

		const cfg: PhotoEditorConfig = this.cfg;
		const ar: number = this.effectiveAspectRatio();
		const recenterImage: boolean = options?.recenterImage ?? true;
		const constrainToCanvasPadding: boolean = options?.constrainToCanvasPadding ?? false;

		if (ar != null) {
			if (!canvasEl) return;
			this.runAspectLockedTrim(
				cropImg,
				selection,
				htmlImg,
				canvasEl,
				nw,
				nh,
				ar,
				cfg,
				recenterImage,
				constrainToCanvasPadding
			);
			return;
		}

		const contentNatural: AxisBounds = resolveTrimContentNatural(
			htmlImg,
			nw,
			nh,
			cfg.autoTrim,
			cfg.layout.minContentDimPx,
			false
		);
		if (!contentNatural) return;

		const box: CanvasSelectionRect = mapNaturalRectToCanvasSelectionSpace(
			cropImg,
			nw,
			nh,
			contentNatural.left,
			contentNatural.top,
			contentNatural.right,
			contentNatural.bottom,
			cfg.layout.minCropRectPx
		);
		if (!box) return;
		selection.aspectRatio = Number.NaN;
		selection.$change(box.sx, box.sy, box.sw, box.sh);
	}

	private runAspectLockedTrim(
		cropImg: CropperImageElement,
		selection: CropperSelectionLike,
		htmlImg: HTMLImageElement,
		canvasEl: CropperCanvasLike,
		nw: number,
		nh: number,
		ar: number,
		cfg: PhotoEditorConfig,
		recenterImage: boolean,
		constrainToCanvasPadding: boolean
	): void {
		if (recenterImage) {
			this.fitImageContain();
		}
		const fullImageBox: CanvasSelectionRect = mapNaturalRectToCanvasSelectionSpace(
			cropImg,
			nw,
			nh,
			0,
			0,
			nw,
			nh,
			cfg.layout.minCropRectPx
		);
		if (!fullImageBox) {
			this.syncCropperImageTransformFlags();
			return;
		}

		const contentNatural: AxisBounds = resolveTrimContentNatural(
			htmlImg,
			nw,
			nh,
			cfg.autoTrim,
			cfg.layout.minContentDimPx,
			true
		);
		const canvasW: number = Math.max(canvasEl.offsetWidth, 1);
		const canvasH: number = Math.max(canvasEl.offsetHeight, 1);
		const rawPad: number = constrainToCanvasPadding ? Math.max(0, cfg.layout.imageEdgePaddingPx) : 0;
		const padX: number = Math.min(rawPad, Math.max(0, (canvasW - cfg.layout.minCropRectPx) / 2));
		const padY: number = Math.min(rawPad, Math.max(0, (canvasH - cfg.layout.minCropRectPx) / 2));
		const canvasBounds: AxisBounds = {
			left: padX,
			top: padY,
			right: canvasW - padX,
			bottom: canvasH - padY
		};

		const mapped: AxisBounds = maxInscribedRectWithAspectRatio(
			{
				left: canvasBounds.left,
				top: canvasBounds.top,
				right: canvasBounds.right,
				bottom: canvasBounds.bottom
			},
			ar
		);
		if (!mapped) {
			this.syncCropperImageTransformFlags();
			return;
		}
		const fitted: PhotoEditorSelectionRect = this.fitSelectionRectIntoBounds(
			{
				x: mapped.left,
				y: mapped.top,
				w: mapped.right - mapped.left,
				h: mapped.bottom - mapped.top
			},
			canvasBounds,
			cfg.layout.minCropRectPx
		);
		if (!fitted) {
			this.syncCropperImageTransformFlags();
			return;
		}

		selection.aspectRatio = ar;
		selection.resizable = false;
		selection.movable = false;
		selection.$change?.(fitted.x, fitted.y, fitted.w, fitted.h);

		if (recenterImage && contentNatural) {
			let liveContentBox: CanvasSelectionRect = mapNaturalRectToCanvasSelectionSpace(
				cropImg,
				nw,
				nh,
				contentNatural.left,
				contentNatural.top,
				contentNatural.right,
				contentNatural.bottom,
				cfg.layout.minCropRectPx
			);
			if (liveContentBox) {
				const containScaleRaw: number = Math.min(
					fitted.w / Math.max(liveContentBox.sw, 1e-6),
					fitted.h / Math.max(liveContentBox.sh, 1e-6)
				);
				const containScale: number = Math.min(
					Math.max(containScaleRaw, cfg.trim.containScaleMin),
					cfg.trim.containScaleMax
				);
				if (Math.abs(containScale - 1) > 1e-3) {
					const cw: number = Math.max(canvasEl.offsetWidth, 1);
					const ch: number = Math.max(canvasEl.offsetHeight, 1);
					const canvasRect: DOMRect = canvasEl.getBoundingClientRect();
					const imgRect: DOMRect = (cropImg as HTMLElement).getBoundingClientRect();
					const pivotX: number =
						canvasRect.left + ((fitted.x + fitted.w / 2) * canvasRect.width) / cw - imgRect.left;
					const pivotY: number =
						canvasRect.top + ((fitted.y + fitted.h / 2) * canvasRect.height) / ch - imgRect.top;
					cropImg.$zoom?.(cropperZoomArgumentForUniformScale(containScale), pivotX, pivotY);
				}
				liveContentBox = mapNaturalRectToCanvasSelectionSpace(
					cropImg,
					nw,
					nh,
					contentNatural.left,
					contentNatural.top,
					contentNatural.right,
					contentNatural.bottom,
					cfg.layout.minCropRectPx
				);
				if (liveContentBox) {
					const cw: number = Math.max(canvasEl.offsetWidth, 1);
					const ch: number = Math.max(canvasEl.offsetHeight, 1);
					const canvasRect: DOMRect = canvasEl.getBoundingClientRect();
					const dCanvasX: number = fitted.x + fitted.w / 2 - (liveContentBox.sx + liveContentBox.sw / 2);
					const dCanvasY: number = fitted.y + fitted.h / 2 - (liveContentBox.sy + liveContentBox.sh / 2);
					const moveDx: number = (dCanvasX / cw) * canvasRect.width;
					const moveDy: number = (dCanvasY / ch) * canvasRect.height;
					if (Math.abs(moveDx) > 0.25 || Math.abs(moveDy) > 0.25) {
						cropImg.$move?.(moveDx, moveDy);
					}
				}
			}
		}

		cropImg.scalable = true;
		cropImg.translatable = true;
		this.syncCropperImageTransformFlags();
	}

	private fitSelectionRectIntoBounds(
		rect: PhotoEditorSelectionRect,
		bounds: AxisBounds,
		minRectPx: number
	): PhotoEditorSelectionRect {
		const maxW: number = Math.max(bounds.right - bounds.left, 0);
		const maxH: number = Math.max(bounds.bottom - bounds.top, 0);
		const w: number = Math.min(rect.w, maxW);
		const h: number = Math.min(rect.h, maxH);
		if (w < minRectPx || h < minRectPx) return null;
		const xMin: number = bounds.left;
		const yMin: number = bounds.top;
		const xMax: number = bounds.right - w;
		const yMax: number = bounds.bottom - h;
		const x: number = Math.min(Math.max(rect.x, xMin), xMax);
		const y: number = Math.min(Math.max(rect.y, yMin), yMax);
		return { x, y, w, h };
	}

	private fitImageContain(): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		if (!cropImg?.$center) return;
		cropImg.scalable = true;
		cropImg.translatable = true;
		cropImg.initialCenterSize = 'contain';
		cropImg.$resetTransform?.();
		cropImg.$center('contain');
		this.fitImageIntoCanvasWithPadding();
		this.nudgeCropperImageToCanvasCenter();
		this.captureBaseImageScale();
		this.syncCropperImageTransformFlags();
	}

	private fitImageIntoCanvasWithPadding(): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		if (!cropImg?.$zoom || !canvas) return;
		const imageRect: DOMRect = (cropImg as HTMLElement).getBoundingClientRect();
		const canvasRect: DOMRect = canvas.getBoundingClientRect();
		if (imageRect.width <= 0 || imageRect.height <= 0 || canvasRect.width <= 0 || canvasRect.height <= 0) {
			return;
		}
		const pad: number = Math.max(0, this.cfg.layout.imageEdgePaddingPx);
		const maxWidth: number = Math.max(canvasRect.width - pad * 2, 1);
		const maxHeight: number = Math.max(canvasRect.height - pad * 2, 1);
		const scaleToPadding: number = Math.min(maxWidth / imageRect.width, maxHeight / imageRect.height, 1);
		if (!Number.isFinite(scaleToPadding) || scaleToPadding >= 0.999) return;
		cropImg.$zoom(
			cropperZoomArgumentForUniformScale(scaleToPadding),
			imageRect.width / 2,
			imageRect.height / 2
		);
	}

	private nudgeCropperImageToCanvasCenter(): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		const selection: CropperSelectionLike = this.getCropperSelection();
		if (!cropImg?.$move || !canvas) return;
		cropImg.scalable = true;
		cropImg.translatable = true;
		const imgRect: DOMRect = (cropImg as HTMLElement).getBoundingClientRect();
		const canvasRect: DOMRect = canvas.getBoundingClientRect();
		const selectionCenterX: number = selection
			? selection.x + selection.width / 2
			: canvasRect.width / 2;
		const selectionCenterY: number = selection
			? selection.y + selection.height / 2
			: canvasRect.height / 2;
		const dx: number =
			canvasRect.left + selectionCenterX - (imgRect.left + imgRect.width / 2);
		const dy: number =
			canvasRect.top + selectionCenterY - (imgRect.top + imgRect.height / 2);
		if (Math.abs(dx) > 0.25 || Math.abs(dy) > 0.25) cropImg.$move(dx, dy);
	}

	private stabilizeImageCenterAfterLayout(gen: number, onDone?: () => void): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		const apply: () => void = (): void => {
			if (gen !== this.mountGeneration || !this.cropper) return;
			this.fitImageContain();
		};
		const nudgeOnly: () => void = (): void => {
			if (gen !== this.mountGeneration || !this.cropper) return;
			this.nudgeCropperImageToCanvasCenter();
			this.syncCropperImageTransformFlags();
		};
		requestAnimationFrame((): void => {
			requestAnimationFrame((): void => {
				apply();
				const afterDom: () => void = (): void => {
					nudgeOnly();
					requestAnimationFrame((): void => {
						nudgeOnly();
						if (gen !== this.mountGeneration || !this.cropper) return;
						if (this.isAspectLocked()) this.autoTrimEmptyEdges();
						onDone?.();
					});
				};
				if (cropImg?.$nextTick) void cropImg.$nextTick(afterDom);
				else queueMicrotask(afterDom);
			});
		});
	}

	private attachCanvasResizeObserver(): void {
		this.detachCanvasResizeObserver();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		if (!canvas) return;
		this.attachCanvasPointerRefresh(canvas);
		if (typeof ResizeObserver === 'undefined') return;
		let skipInitialFire: boolean = true;
		this.canvasResizeObserver = new ResizeObserver((): void => {
			if (skipInitialFire) {
				skipInitialFire = false;
				return;
			}
			if (this.resizeDebounceTimer !== null) clearTimeout(this.resizeDebounceTimer);
			this.resizeDebounceTimer = setTimeout((): void => {
				this.resizeDebounceTimer = null;
				if (!this.cropper) return;
				this.fitImageContain();
				this.configureSelection({ autoTrimEdges: true });
			}, 80);
		});
		this.canvasResizeObserver.observe(canvas);
	}

	private attachCanvasPointerRefresh(canvas: HTMLElement): void {
		this.detachCanvasPointerRefresh();
		const cancelPanStretchRaf: () => void = (): void => {
			if (this.canvasPanStretchRefreshRafId != null) {
				cancelAnimationFrame(this.canvasPanStretchRefreshRafId);
				this.canvasPanStretchRefreshRafId = null;
			}
		};
		let wheelSyncRafId: number | null = null;
		const scheduleStateSync: () => void = (): void => {
			if (wheelSyncRafId != null) return;
			wheelSyncRafId = requestAnimationFrame((): void => {
				wheelSyncRafId = null;
				this.clampSelectionToCanvas();
				this.refreshStretchHandles();
				this.syncTransformState();
			});
		};
		const onPointerMoveCapture: (event: PointerEvent) => void = (event: PointerEvent): void => {
			this.clampSelectionToCanvas();
			if (!this.imageStretchMode() || this.stretchDragSession || !this.imagePanMode()) {
				cancelPanStretchRaf();
				return;
			}
			if (event.pointerType === 'mouse' && (event.buttons & 1) === 0) {
				cancelPanStretchRaf();
				return;
			}
			if (this.canvasPanStretchRefreshRafId != null) return;
			this.canvasPanStretchRefreshRafId = requestAnimationFrame((): void => {
				this.canvasPanStretchRefreshRafId = null;
				if (!this.imageStretchMode() || this.stretchDragSession) return;
				this.refreshStretchHandles();
			});
		};
		const onInteractionEnd: () => void = (): void => {
			cancelPanStretchRaf();
			this.clampSelectionToCanvas();
			this.syncTransformState();
			if (!this.imageStretchMode() || this.stretchDragSession) return;
			this.scheduleStretchHandlesRefresh();
		};
		const onWheel: () => void = (): void => {
			scheduleStateSync();
		};
		canvas.addEventListener('pointermove', onPointerMoveCapture, { passive: true, capture: true });
		canvas.addEventListener('pointerup', onInteractionEnd);
		canvas.addEventListener('pointercancel', onInteractionEnd);
		canvas.addEventListener('lostpointercapture', onInteractionEnd);
		canvas.addEventListener('wheel', onWheel, { passive: true });
		this.canvasPointerRefreshCleanup = (): void => {
			cancelPanStretchRaf();
			if (wheelSyncRafId != null) {
				cancelAnimationFrame(wheelSyncRafId);
				wheelSyncRafId = null;
			}
			canvas.removeEventListener('pointermove', onPointerMoveCapture, true);
			canvas.removeEventListener('pointerup', onInteractionEnd);
			canvas.removeEventListener('pointercancel', onInteractionEnd);
			canvas.removeEventListener('lostpointercapture', onInteractionEnd);
			canvas.removeEventListener('wheel', onWheel);
			this.canvasPointerRefreshCleanup = null;
		};
	}

	private detachCanvasPointerRefresh(): void {
		this.canvasPointerRefreshCleanup?.();
	}

	private detachCanvasResizeObserver(): void {
		if (this.resizeDebounceTimer !== null) {
			clearTimeout(this.resizeDebounceTimer);
			this.resizeDebounceTimer = null;
		}
		this.detachCanvasPointerRefresh();
		this.canvasResizeObserver?.disconnect();
		this.canvasResizeObserver = null;
	}

	private destroy(): void {
		this.cancelStretchDragSession();
		this.stretchHandlePoints.set([]);
		this.stretchFallbackTransform = null;
		this.detachCanvasResizeObserver();
		this.cropper?.destroy();
		this.cropper = null;
	}

	private revokeResolvedBlobUrl(): void {
		revokeBlobObjectUrl(this.resolvedBlobUrl);
		this.resolvedBlobUrl = null;
	}

	private async resolveEditorImageSrc(src: string): Promise<void> {
		const token: number = ++this.editorSrcResolveToken;
		const raw: string = src?.trim() ?? '';
		if (!raw) {
			this.revokeResolvedBlobUrl();
			this.editorImageSrc.set('');
			return;
		}
		if (raw.startsWith('data:') || raw.startsWith('blob:')) {
			this.revokeResolvedBlobUrl();
			this.editorImageSrc.set(raw);
			return;
		}
		try {
			const response: Response = await fetch(raw, {
				credentials: 'omit',
				mode: 'cors',
				cache: 'no-store'
			});

			if (!response.ok) return;

			const blob: Blob = await response.blob();

			if (!blob.size) return;

			if (token !== this.editorSrcResolveToken) return;

			const objectUrl: string = URL.createObjectURL(blob);

			this.revokeResolvedBlobUrl();
			this.resolvedBlobUrl = objectUrl;
			this.editorImageSrc.set(objectUrl);

			return;
		} catch (_error: unknown) {}
		if (token !== this.editorSrcResolveToken) return;
		this.revokeResolvedBlobUrl();
		this.editorImageSrc.set(raw);
	}

	onStretchHandlePointerDown(event: PointerEvent, handle: PhotoEditorStretchHandle): void {
		if (!this.imageStretchMode() || this.loading()) return;
		const frame: PhotoEditorImageFrame = this.resolveImageFrame();
		const transform: TransformMatrix2D = this.resolveImageTransformMatrix();
		if (!frame || !transform) return;
		event.preventDefault();
		event.stopPropagation();
		this.stretchDragSession = {
			pointerId: event.pointerId,
			handle,
			startPointerX: event.clientX,
			startPointerY: event.clientY,
			transform,
			frame
		};
		window.addEventListener('pointermove', this.onWindowPointerMoveBound, { passive: false });
		window.addEventListener('pointerup', this.onWindowPointerUpBound);
		window.addEventListener('pointercancel', this.onWindowPointerUpBound);
	}

	private onStretchPointerMove(event: PointerEvent): void {
		const session: PhotoEditorStretchSession = this.stretchDragSession;
		if (!session || event.pointerId !== session.pointerId) return;
		event.preventDefault();
		const next: TransformMatrix2D = transformAfterStretchDrag({
			handle: session.handle,
			startPointerX: session.startPointerX,
			startPointerY: session.startPointerY,
			pointerX: event.clientX,
			pointerY: event.clientY,
			transform: session.transform,
			frame: session.frame,
			minScale: this.minStretchScale
		});
		if (!next) return;
		this.setImageTransformMatrix(next);
		this.refreshStretchHandles();
		this.syncTransformState();
	}

	private onStretchPointerUpOrCancel(event: PointerEvent): void {
		const session: PhotoEditorStretchSession = this.stretchDragSession;
		if (!session || event.pointerId !== session.pointerId) return;
		this.cancelStretchDragSession();
		this.refreshStretchHandles();
		this.syncCropperImageTransformFlags();
	}

	private cancelStretchDragSession(): void {
		this.stretchDragSession = null;
		window.removeEventListener('pointermove', this.onWindowPointerMoveBound);
		window.removeEventListener('pointerup', this.onWindowPointerUpBound);
		window.removeEventListener('pointercancel', this.onWindowPointerUpBound);
	}

	private resolveImageTransformMatrix(): TransformMatrix2D {
		const cropImg: CropperImageElement = this.getCropperImage();
		const transform: number[] = cropImg?.$getTransform?.();
		if (transform != null && transform.length >= 6) {
			const [a, b, c, d, e, f] = transform as number[];
			if ([a, b, c, d, e, f].every((v: number): boolean => Number.isFinite(v))) {
				return { a, b, c, d, e, f };
			}
		}
		if (this.stretchFallbackTransform) return this.stretchFallbackTransform;
		if (cropImg && this.imageStretchMode()) {
			return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
		}
		return null;
	}

	private resolveImageFrame(): PhotoEditorImageFrame {
		const cropImg: CropperImageElement = this.getCropperImage();
		const htmlImg: HTMLImageElement = this.editorImage?.nativeElement;
		if (!cropImg || !htmlImg) return null;
		const inner: HTMLImageElement = cropImg.$image;
		const width: number = inner?.naturalWidth ?? htmlImg.naturalWidth;
		const height: number = inner?.naturalHeight ?? htmlImg.naturalHeight;
		if (width <= 0 || height <= 0) return null;
		return {
			width,
			height,
			offsetLeft: cropImg.offsetLeft,
			offsetTop: cropImg.offsetTop
		};
	}

	private setImageTransformMatrix(matrix: TransformMatrix2D): void {
		const cropImg: CropperImageElement = this.getCropperImage();
		if (!cropImg) return;
		cropImg.scalable = true;
		cropImg.translatable = false;
		this.stretchFallbackTransform = matrix;
		if (cropImg.$setTransform) {
			cropImg.$setTransform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f);
			return;
		}
		cropImg.style.transform = `matrix(${matrix.a}, ${matrix.b}, ${matrix.c}, ${matrix.d}, ${matrix.e}, ${matrix.f})`;
	}

	private refreshStretchHandles(): void {
		if (!this.imageStretchMode()) {
			this.stretchHandlePoints.set([]);
			return;
		}
		const transform: TransformMatrix2D = this.resolveImageTransformMatrix();
		const frame: PhotoEditorImageFrame = this.resolveImageFrame();
		const canvas: CropperCanvasLike = this.getCropperCanvas();
		if (!transform || !frame) {
			this.stretchHandlePoints.set([]);
			return;
		}
		const points: ReadonlyArray<PhotoEditorStretchHandlePoint> = resolveStretchHandles(transform, frame);
		if (!canvas) {
			this.stretchHandlePoints.set(points);
			return;
		}
		const clampPad: number = 8;
		const minX: number = clampPad;
		const minY: number = clampPad;
		const maxX: number = Math.max(canvas.offsetWidth - clampPad, minX);
		const maxY: number = Math.max(canvas.offsetHeight - clampPad, minY);
		this.stretchHandlePoints.set(
			points.map((point: PhotoEditorStretchHandlePoint): PhotoEditorStretchHandlePoint & VectorAxis => ({
				handle: point.handle,
				x: Math.min(Math.max(point.x, minX), maxX),
				y: Math.min(Math.max(point.y, minY), maxY)
			}))
		);
	}

	private scheduleStretchHandlesRefresh(): void {
		requestAnimationFrame((): void => this.refreshStretchHandles());
	}

	private registerSignals(): void {
		runInInjectionContext(this.injector, (): void => {
			let prevSrc: string = undefined;
			effect((): void => {
				const src: string = this.imageSrc();
				if (prevSrc === undefined) {
					prevSrc = src;
					return;
				}
				if (src === prevSrc) return;
				prevSrc = src;
				untracked((): void => {
					void this.resolveEditorImageSrc(src);
					this.destroy();
					this.loading.set(Boolean(src));
				});
			});

			let aspectEffectInitialized: boolean = false;
			let prevAspect: number;
			effect((): void => {
				const ar: number = this.effectiveAspectRatio();
				if (!aspectEffectInitialized) {
					aspectEffectInitialized = true;
					prevAspect = ar;
					return;
				}
				if (ar === prevAspect) return;
				prevAspect = ar;
				untracked((): void => {
					if (!this.cropper || !this.editorImage?.nativeElement) return;
					this.configureSelection({ autoTrimEdges: true });
				});
			});
		});
	}
}
